import { useMemo, useState } from 'react';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@artstorefronts/ui';
import { ChevronsUpDown } from 'lucide-react';
import {
  groupedEvents,
  type AmplitudeApp,
  type AmplitudeEventGroupDisplay,
} from '@arthelper/shared/amplitude-event-names';

const APP_ORDER: AmplitudeApp[] = ['client', 'town_square', 'api'];

const APP_LABELS: Record<AmplitudeApp, string> = {
  client: 'Main app',
  town_square: 'Community site',
  api: 'Server',
};

/**
 * Mirrors `assertTargetEventName` in api/src/services/feature-wizard/admin.ts, which is the
 * authority: the events THIS feature emits are in the catalog but can never be a target, since
 * the held-out control audience is served no overlay and so never fires one. Keyed on the group,
 * not the names, so a wizard event added later drops out of the picker for free.
 */
const OWN_EVENT_GROUP = 'feature_wizard';

type OptionGroup = { label: string; names: string[] };

function appsLabel(apps: readonly AmplitudeApp[]): string {
  return APP_ORDER.filter((app) => apps.includes(app))
    .map((app) => APP_LABELS[app])
    .join(' & ');
}

/** One group's events partitioned by app, name-sorted by heading. */
function byApps({ group, events }: AmplitudeEventGroupDisplay): OptionGroup[] {
  const buckets = events.reduce((memo, event) => {
    const label = `${appsLabel(event.apps)} · ${group}`;
    memo.set(label, [...(memo.get(label) ?? []), event.name]);
    return memo;
  }, new Map<string, string[]>());

  return [...buckets.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([label, names]) => ({ label, names }));
}

/**
 * The catalog partitioned by app AND group, so an event fired by both apps sits in its own
 * heading rather than being filed under whichever app happened to declare it first. This
 * feature's own group is dropped: it cannot be a valid target, so it must never be offered.
 *
 * Filtering as the teammate types is `Command`'s job, not ours — it matches on each item's
 * value, which is the event name plus its heading.
 */
function optionGroups(): OptionGroup[] {
  return groupedEvents()
    .filter((display) => display.group !== OWN_EVENT_GROUP)
    .flatMap(byApps);
}

/**
 * Names an event the product ALREADY fires. A free-text box here would produce a wizard that
 * reads zero for both audiences with nothing to warn the teammate, so the only way to set this
 * field is to choose a catalog name: typing filters the list, it never becomes the value.
 */
export function TargetEventSelect({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  const [open, setOpen] = useState(false);
  const groups = useMemo(optionGroups, []);
  const total = groups.reduce((sum, group) => sum + group.names.length, 0);

  return (
    <div>
      <Label htmlFor="wizard-target-event" className="mb-1 block text-sm font-medium">
        Action this wizard should drive
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="wizard-target-event"
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={`w-full justify-between font-mono text-sm ${value ? '' : 'text-gray-500'}`}
          >
            {value || 'Choose the event this wizard should move…'}
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
          <Command>
            <CommandInput placeholder="Type to filter events…" />
            <CommandList>
              <CommandEmpty>
                No event matches. It has to be added to the shared catalog by an engineer first.
              </CommandEmpty>
              {groups.map((group) => (
                <CommandGroup key={group.label} heading={group.label}>
                  {group.names.map((name) => (
                    <CommandItem
                      key={name}
                      value={`${name} ${group.label}`}
                      className="font-mono text-sm"
                      onSelect={() => {
                        onChange(name);
                        setOpen(false);
                      }}
                    >
                      {name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <p className="mt-1 text-xs leading-relaxed text-gray-500">
        Pick from the {total} events the product already sends — the wizard never fires this one, it only reads it back
        split by audience. The wizard&rsquo;s own <code className="font-mono">feature_wizard_*</code> events are left
        out on purpose: the held-out audience never fires them, so there would be nothing to compare against. An event
        that is not listed has to be added to the shared catalog by an engineer first.
      </p>
    </div>
  );
}
